import * as React from 'react';

import { getLocalTimeZone, now } from '@internationalized/date';
import {
  enumCurrencyCode,
  enumWeightDisplayUnit,
} from '@rematter/frontend-lib-api-client';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { purchaseLoadCreateAtom } from '../../../_graphql/mutationPurchaseLoadCreate';
import { $inboundTicketLoads } from '../../../_state/scaleTicketPro.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { tradeRoutePaths as tradeRoutes } from '../../../routes';

import { useIdentity } from '~/shared/hooks';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

export const useCreateInboundTicket = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();
  const toast = useToast();
  const { facilityId } = useIdentity();

  const { refetch: refetchInbound } = useAtomValue($inboundTicketLoads);

  const [isCreating, setIsCreating] = React.useState(false);

  const createTicketMutation = useSetAtom(
    purchaseLoadCreateAtom({
      onSuccess: () => {
        refetchInbound?.();
      },
    }),
  );

  const createTicket = React.useCallback(async () => {
    if (isCreating) return;

    if (!facilityId) {
      toast.error(t.scaleTicketPro.noFacilitiesAvailable);
      return;
    }

    setIsCreating(true);
    try {
      const result = await createTicketMutation({
        receivedDate: now(getLocalTimeZone()),
        facilityId,
        currencyCode: enumCurrencyCode.USD,
        weightDisplayUnit: enumWeightDisplayUnit.ScaleUnit,
      });

      if (result?.tradePurchaseLoadCreate?.id) {
        navigate(
          tradeRoutes.scaleTicketDetail.replace(
            ':id',
            result.tradePurchaseLoadCreate.id,
          ),
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to create ticket',
      );
    } finally {
      setIsCreating(false);
    }
  }, [isCreating, facilityId, createTicketMutation, navigate, toast, t]);

  return { createTicket, isCreating };
};
